if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.detachedMainFragment = null;
        this.detachedThumbsFragment = null;
      }

      connectedCallback() {
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      ensureElements() {
        if (!this.elements) {
          this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        }
      }

      isColorFilterEnabled() {
        return this.dataset.colorFilterEnabled === 'true';
      }

      getColorOptionPosition() {
        return parseInt(this.dataset.colorOptionPosition || '0', 10) || 0;
      }

      getDetachedMain() {
        if (!this.detachedMainFragment) {
          this.detachedMainFragment = document.createDocumentFragment();
        }
        return this.detachedMainFragment;
      }

      getDetachedThumbs() {
        if (!this.detachedThumbsFragment) {
          this.detachedThumbsFragment = document.createDocumentFragment();
        }
        return this.detachedThumbsFragment;
      }

      getThumbWrapper() {
        return this.elements?.thumbnails?.querySelector('.swiper-wrapper') || this.querySelector('[id^="Slider-Thumbnails"]');
      }

      collectMainSlidesSorted() {
        this.ensureElements();
        const mediaList = this.elements?.mediaList;
        if (!mediaList) return [];
        const frag = this.detachedMainFragment;
        const fromList = [...mediaList.querySelectorAll(':scope > .product__media-item')];
        const fromFrag = frag ? [...frag.querySelectorAll('.product__media-item')] : [];
        return [...fromList, ...fromFrag].sort((a, b) => Number(a.dataset.mediaIndex) - Number(b.dataset.mediaIndex));
      }

      collectThumbSlidesSorted() {
        this.ensureElements();
        const thumbWrap = this.getThumbWrapper();
        if (!thumbWrap) return [];
        const frag = this.detachedThumbsFragment;
        const fromList = [...thumbWrap.querySelectorAll(':scope > .product__thumbs-item')];
        const fromFrag = frag ? [...frag.querySelectorAll('.product__thumbs-item')] : [];
        return [...fromList, ...fromFrag].sort((a, b) => Number(a.dataset.mediaPosition) - Number(b.dataset.mediaPosition));
      }

      restoreAllMediaSlides() {
        this.ensureElements();
        const mediaList = this.elements?.mediaList;
        if (!mediaList) return;

        const slides = this.collectMainSlidesSorted();
        this.sonaSirala(mediaList, slides);

        const thumbWrap = this.getThumbWrapper();
        if (thumbWrap) {
          const thumbs = this.collectThumbSlidesSorted();
          this.sonaSirala(thumbWrap, thumbs);
        }

        this.refreshSwiper();
      }

      getColorValueFromVariant(variant) {
        const pos = this.getColorOptionPosition();
        if (!variant || !pos) return '';
        if (pos === 1) return (variant.option1 || '').trim();
        if (pos === 2) return (variant.option2 || '').trim();
        if (pos === 3) return (variant.option3 || '').trim();
        return '';
      }

      /** Lowercase trimmed color option from variant picker */
      normalizedColorKey(colorValue) {
        return (colorValue || '').trim().toLowerCase();
      }

      /**
       * Shared technical drawing image: same alt on all products, show for every color at the end.
       * Accepts "Teknik Çizim" / "Teknik Cizim" (spacing + Turkish ç/c).
       */
      isTechnicalDrawingAlt(altRaw) {
        const key = (altRaw || '')
          .trim()
          .toLowerCase()
          .replace(/\s+/g, ' ')
          .replace(/ç/g, 'c');
        return key === 'teknik cizim';
      }

      /** Keep original relative order; move all technical-drawing slides to the end */
      orderWithTechnicalDrawingsLast(slides) {
        const rest = [];
        const tech = [];
        for (const s of slides) {
          if (this.isTechnicalDrawingAlt(s.dataset.mediaAlt)) tech.push(s);
          else rest.push(s);
        }
        return [...rest, ...tech];
      }

      /** data-media-colors uses "|Red|" "|Chrome|" segments from Liquid */
      colorsPipeIncludes(colorsAttr, colorValue) {
        const target = this.normalizedColorKey(colorValue);
        if (!target || !colorsAttr) return false;
        return colorsAttr
          .split('|')
          .map((s) => s.trim().toLowerCase())
          .some((c) => c && c === target);
      }

      /**
       * Fallback when media is not tied to a variant image: alt must identify the color.
       * Accepts exact match (any case), comma/pipe/semicolon-separated tokens, or whole-word-ish substring.
       */
      altMatchesColor(altRaw, colorValue) {
        const target = this.normalizedColorKey(colorValue);
        if (!target) return false;
        const alt = (altRaw || '').trim().toLowerCase();
        if (!alt) return false;
        if (alt === target) return true;
        const segments = alt.split(/[,;|]/).map((s) => s.trim().toLowerCase()).filter(Boolean);
        if (segments.some((s) => s === target)) return true;
        const escaped = target.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const re = new RegExp(`(^|[\\s_/\\-])${escaped}([\\s_/\\-]|$)`, 'i');
        return re.test((altRaw || '').trim());
      }

      slideMatchesSelectedColor(slide, colorValue) {
        if (this.isTechnicalDrawingAlt(slide.dataset.mediaAlt)) return false;
        const colors = slide.dataset.mediaColors || '';
        if (colors && this.colorsPipeIncludes(colors, colorValue)) return true;
        return this.altMatchesColor(slide.dataset.mediaAlt || '', colorValue);
      }

      thumbMatchesSelectedColor(thumb, colorValue, visibleIds) {
        if (!visibleIds.has(String(thumb.dataset.target))) return false;
        if (thumb.dataset.target === 'konfigurator') return true;
        if (this.isTechnicalDrawingAlt(thumb.dataset.mediaAlt)) return true;
        return this.slideMatchesSelectedColor(thumb, colorValue);
      }

      applyColorFilter(variant) {
        const color = this.getColorValueFromVariant(variant);
        this.applyColorFilterByValue(color);
      }

      applyColorFilterByValue(colorValue) {
        this.ensureElements();

        if (!this.isColorFilterEnabled()) {
          this.restoreAllMediaSlides();
          return;
        }

        const colorNorm = (colorValue || '').trim();
        const mediaList = this.elements?.mediaList;
        if (!mediaList) return;

        let slides = this.collectMainSlidesSorted();
        const konfSlides = slides.filter((s) => s.dataset.mediaType === 'konfigurator');
        const mediaSlides = slides.filter((s) => s.dataset.mediaType !== 'konfigurator');
        const techSlides = mediaSlides.filter((s) => this.isTechnicalDrawingAlt(s.dataset.mediaAlt));
        const nonTechSlides = mediaSlides.filter((s) => !this.isTechnicalDrawingAlt(s.dataset.mediaAlt));

        let colorMatching = nonTechSlides.filter((s) => this.slideMatchesSelectedColor(s, colorNorm));

        let matching;
        if (colorMatching.length === 0) {
          matching = [...this.orderWithTechnicalDrawingsLast(mediaSlides), ...konfSlides];
        } else {
          matching = [...colorMatching, ...techSlides, ...konfSlides];
        }

        const visibleIds = new Set(matching.map((s) => String(s.dataset.mediaId)));

        slides.filter((s) => !matching.includes(s)).forEach((s) => this.getDetachedMain().appendChild(s));
        this.sonaSirala(mediaList, matching);

        const thumbWrap = this.getThumbWrapper();
        if (thumbWrap) {
          let thumbs = this.collectThumbSlidesSorted();
          let matchingThumbs = thumbs.filter((t) => this.thumbMatchesSelectedColor(t, colorNorm, visibleIds));
          if (matchingThumbs.length === 0) {
            matchingThumbs = thumbs;
          } else {
            const orderMap = new Map(matching.map((s, i) => [String(s.dataset.mediaId), i]));
            matchingThumbs.sort(
              (a, b) =>
                (orderMap.get(String(a.dataset.target)) ?? 999) - (orderMap.get(String(b.dataset.target)) ?? 999)
            );
          }
          thumbs.filter((t) => !matchingThumbs.includes(t)).forEach((t) => this.getDetachedThumbs().appendChild(t));
          this.sonaSirala(thumbWrap, matchingThumbs);
        }

        this.refreshSwiper();
      }

      sonaSirala(parent, list) {
        const kids = parent.children;
        const bas = kids.length - list.length;
        if (bas >= 0 && list.every((el, i) => kids[bas + i] === el)) return;
        list.forEach((el) => parent.appendChild(el));
      }

      refreshSwiper() {
        if (this.sliderInstance && this.sliderInstance.slider?.update) {
          this.sliderInstance.slider.update();
        }
        if (this.thumbsInstance && this.thumbsInstance.slider?.update) {
          this.thumbsInstance.slider.update();
        }
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;

        // Lightbox dataSource'u TÜM renk medyasından kur (renk filtresi DOM'dan slide
        // koparmadan ÖNCE) ki büyütünce ›/‹ ile diğer renk fotoğraflarına geçilebilsin.
        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        if (this.isColorFilterEnabled()) {
          this.applyColorFilterByValue((this.dataset.initialSelectedColor || '').trim());
        }

        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal',
            },
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5,
              },
              1536: {
                slidesPerView: 7,
              },
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        if (this.lightbox) return;
        let dataSource = [];
        // TÜM medyayı al (mediaList + renk filtresiyle koparılan detached fragment dahil),
        // mediaIndex sırasına göre. Böylece lightbox tüm renk fotoğraflarında ›/‹ ile gezinir.
        let allMedia = [];
        if (typeof this.collectMainSlidesSorted === 'function') {
          allMedia = this.collectMainSlidesSorted();
        }
        if (!allMedia || allMedia.length === 0) {
          allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        }
        allMedia = allMedia.filter((m) => m && !m.classList.contains('swiper-slide-duplicate'));
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'konfigurator':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--konfigurator"><iframe src="${media.dataset.konfUrl}" title="3D Konfigüratör" allow="fullscreen; xr-spatial-tracking" allowfullscreen></iframe></div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        // Store the current index before closing
        let lastLightboxIndex = null;
        this.lightbox.on('close', () => {
          if (this.lightbox.pswp) {
            lastLightboxIndex = this.lightbox.pswp.currIndex;
          }
        });

        // Update slider after lightbox is destroyed
        this.lightbox.on('destroy', () => {
          if (this.sliderInstance && this.sliderInstance.slider && lastLightboxIndex !== null) {
            const slider = this.sliderInstance.slider;
            const targetIndex = lastLightboxIndex;
            // currIndex from lightbox is the index in dataSource array
            // which should match the slider index since both are created from the same media items in the same order
            if (targetIndex >= 0 && targetIndex < slider.slides.length && slider.activeIndex !== targetIndex) {
              // Use requestAnimationFrame to ensure DOM is ready
              requestAnimationFrame(() => {
                slider.slideTo(targetIndex, 0);
              });
            }
            lastLightboxIndex = null;
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          const slide = this.lightbox.pswp?.currSlide;
          if (slide?.data && slide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant) return;

        this.ensureElements();

        if (this.isColorFilterEnabled()) {
          this.applyColorFilter(variant);
        } else {
          this.restoreAllMediaSlides();
        }

        let featuredMedia = variant.featured_media;

        if (!featuredMedia && variant.featured_image) {
          featuredMedia = {
            id: variant.featured_image.id,
            position: variant.featured_image.position || 0,
            media_type: 'image',
          };
        }

        if (!featuredMedia) return;

        if (this.sliderInstance && this.sliderInstance.slider) {
          const slides = [...this.elements.mediaList.querySelectorAll('.product__media-item')];
          let slideIndex = slides.findIndex((s) => String(s.dataset.mediaId) === String(featuredMedia.id));
          if (slideIndex < 0) slideIndex = 0;
          this.sliderInstance.slider.slideTo(slideIndex, 0, false);
        } else {
          this.sortMediaItems(variant, featuredMedia);
        }
      }

      sortMediaItems(variant, featuredMedia) {
        this.ensureElements();

        if (!featuredMedia) {
          featuredMedia =
            variant.featured_media ||
            (variant.featured_image
              ? {
                  id: variant.featured_image.id,
                  position: variant.featured_image.position || 0,
                  media_type: 'image',
                }
              : null);
        }

        if (!featuredMedia) return;

        let newMedias = [...this.elements.mediaList.querySelectorAll(':scope > .product__media-item')];

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == featuredMedia.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${featuredMedia.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
